/**
 * Admin API client — GET-only endpoints for dashboard statistics
 */

const API_URL = process.env.REACT_APP_API_URL || 'https://api.kit8exchange.com';
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY || '';

class AdminAPI {
  constructor() {
    this.token = null;
    this.userId = null;
  }

  setAuth(token, userId) {
    this.token = token;
    this.userId = userId;
  }

  async request(endpoint, params = {}) {
    if (!this.token || !this.userId) {
      throw new Error('Not authenticated');
    }

    const url = new URL(`${API_URL}${endpoint}`);
    if (ADMIN_KEY) {
      url.searchParams.set('adminKey', ADMIN_KEY);
    }
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, value);
      }
    });

    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'omit',
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'X-User-ID': this.userId,
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 401 || response.status === 403) {
      this.token = null;
      this.userId = null;
      throw new Error('ACCESS_DENIED');
    }

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`API error ${response.status}: ${text}`);
    }

    return response.json();
  }

  // ========== Статистика ==========
  getStats(period = 'day') {
    return this.request('/api/admin/stats', { period });
  }

  getDailyStats(days = 7) {
    return this.request('/api/admin/stats/daily', { days });
  }

  getDashboard(days = 30) {
    return this.request('/api/admin/analytics/dashboard', { days });
  }

  getRealtime() {
    return this.request('/api/admin/analytics/realtime');
  }

  getEvents(filters = {}) {
    return this.request('/api/admin/analytics/events', {
      event_type: filters.eventType,
      user_id: filters.userId,
      limit: filters.limit || 100,
    });
  }

  getUserAnalytics(userId) {
    return this.request(`/api/admin/analytics/user/${userId}`);
  }

  // ========== Курсы ==========
  getRateSettings() {
    return this.request('/api/admin/rate-settings');
  }

  // ========== Altyn / KYC ==========
  getAltynBalance() {
    return this.request('/api/admin/altyn/balance');
  }

  checkKyc(phone) {
    return this.request('/api/admin/altyn/check-kyc', { phone });
  }

  getMerchantPhones() {
    return this.request('/api/admin/merchant/phones');
  }

  // ========== Ордера ==========
  getOrders(status) {
    return this.request('/api/admin/orders', { status });
  }

  getPaidOrders() {
    return this.request('/api/admin/orders/paid');
  }

  getOrder(orderId) {
    return this.request(`/api/admin/order/${orderId}`);
  }

  // ========== Пользователи ==========
  getUsers() {
    return this.request('/api/admin/users');
  }

  getUserLimits(userId) {
    return this.request(`/api/admin/users/${userId}/limits`);
  }

  getAllLimits() {
    return this.request('/api/admin/limits');
  }

  // ========== Рефералы ==========
  getReferrals(limit = 100) {
    return this.request('/api/admin/referrals', { limit });
  }

  getLeaderboard() {
    return this.request('/api/admin/referrals/leaderboard');
  }

  getReferralCheck(userId) {
    return this.request(`/api/admin/referral/check/${userId}`);
  }

  getReferralEarnings(filters = {}) {
    return this.request('/api/admin/referral/earnings', {
      status: filters.status,
      limit: filters.limit || 100,
    });
  }
}

const api = new AdminAPI();
export default api;
